<!-- Probably legacy component: imported nowhere (2026-10-11); Table.vue uses TableRows.vue.
     Left in place. -->
<!-- Copyright: Sean O'Donoghue -->

<!-- eslint-disable vue/no-v-html -->
<template>
  <tr v-for="(event, eventIndex) in programDay.events" :key="eventIndex">
    <!-- SESSION ROW -->
    <td v-if="event.type === 'session'" colspan="3">
      <b v-html="normalizedTitle(event)" /><br />

      <span v-if="event.subtitle">
        <i>{{ event.subtitle }}: </i>

        <a v-if="shouldLinkSubtitle(event)" :href="event.url">
          <span v-if="event.name">{{ normalizedName(event) }}</span>
          <span v-else-if="event.venue">{{ event.venue }}</span>
        </a>

        <span v-else-if="isAnyUndecided(event)">
          {{ normalizeUndecided() }}
        </span>

        <span v-else-if="event.name"> ({{ normalizedName(event) }}) </span>

        <span v-else-if="event.venue">
          {{ event.venue }}
        </span>
      </span>
    </td>

    <!-- TIME -->
    <td v-if="event.type !== 'session'">
      {{ formatTime(event.start) }}
      <span v-if="event.end" class="wideScreenOnly"> - {{ formatTime(event.end) }} </span>
    </td>

    <!-- VIDEO -->
    <td v-if="event.type !== 'session'">
      <VideoIcon :event="event" />
    </td>

    <!-- EVENT DETAILS -->
    <td v-if="event.type !== 'session'">
      <span class="event">
        <span v-if="!description" class="arrows" @click="toggleDescription(event.ref)">
          <span
            v-if="event.description"
            :ref="setRef(event.ref + 'RightArrow')"
            class="right chevronArrow" />
          <span
            v-if="event.description"
            :ref="setRef(event.ref + 'DownArrow')"
            class="hide down chevronArrow" />
        </span>

        <span v-if="event.title" class="eventTitle">
          <span v-if="event.type === 'keynote'"><b>Keynote: </b></span>

          <!-- IMPORTANT: title may contain HTML -->
          <span v-html="normalizedTitle(event)" />

          <!-- SPEAKER -->
          <template v-if="showParenUndecided(event)"> ({{ normalizeUndecided() }}) </template>

          <template v-else-if="shouldLinkSpeaker(event)">
            (<a :href="event.url">{{ normalizedName(event) }}</a
            >)
          </template>

          <Slides :name="normalizedName(event)" align="left" />

          <!-- IMPORTANT: description may contain HTML -->
          <p
            v-if="event.description"
            :ref="setRef(event.ref)"
            :class="{hide: !description, description: true}"
            v-html="event.description" />
        </span>
      </span>
    </td>
  </tr>
</template>

<script setup>
const props = defineProps(["programDay", "session", "description", "timeZoneSelected"]);

import Slides from "@/components/People/Person/Slides";
import VideoIcon from "@/components/Program/VideoIcon";
import {reactive} from "vue";

/* ---------- refs ---------- */

const refs = reactive({});

function setRef(index) {
  return (el) => {
    if (el) refs[index] = el;
  };
}

function toggleDescription(refKey) {
  const element = refs[refKey];
  const rightArrow = refs[`${refKey}RightArrow`];
  const downArrow = refs[`${refKey}DownArrow`];
  if (!element) return;
  if (element.classList.contains("hide")) {
    element.classList.remove("hide");
    rightArrow.classList.add("hide");
    downArrow.classList.remove("hide");
    return;
  }
  element.classList.add("hide");
  downArrow.classList.add("hide");
  rightArrow.classList.remove("hide");
}

/* ---------- normalization ---------- */

function normalizeRaw(value) {
  return String(value || "")
    .replace(/\./g, "")
    .replace(/\s+/g, "")
    .toUpperCase();
}

function normalizeUndecided() {
  return "T.B.A.";
}

function isUndecidedValue(value) {
  return normalizeRaw(value) === "TBA";
}

function normalizedName(event) {
  if (isUndecidedValue(event?.name)) return normalizeUndecided();
  return event?.name;
}

function normalizedTitle(event) {
  if (isUndecidedValue(event?.title)) return normalizeUndecided();
  return event?.title;
}

/* ---------- undecided state ---------- */

function isNameUndecided(event) {
  return isUndecidedValue(event?.name);
}

function isTitleUndecided(event) {
  return isUndecidedValue(event?.title);
}

function isAnyUndecided(event) {
  return isNameUndecided(event) || isTitleUndecided(event);
}

/* ---------- rendering rules ---------- */

function showParenUndecided(event) {
  return isNameUndecided(event) && !isTitleUndecided(event);
}

function shouldLinkSubtitle(event) {
  return !!event.subtitle && !!event.url && !isAnyUndecided(event);
}

function shouldLinkSpeaker(event) {
  return !!event.name && !!event.url && !isAnyUndecided(event);
}

/* ---------- time ---------- */

function formatTime(date) {
  if (!date) return;
  if (!props.timeZoneSelected) return;
  if (props.timeZoneSelected.match(/London|Australia|America/)) {
    return date.toFormat("h:mma").toLowerCase();
  }
  return date.toFormat("T").toLowerCase();
}
</script>

<style scoped>
#programTable {
  clear: both; /* https://stackoverflow.com/q/2580772 */
  margin-top: 3.8em;
  margin-bottom: 2em;
}
tr:nth-child(even) {
  /*  https://stackoverflow.com/q/32867605 */
  background-color: var(--background-color-subtle);
}
.eventTable {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 1em;
}
.eventTable td,
th {
  padding: 6px;
  vertical-align: top;
}
/* https://stackoverflow.com/q/27788092 */
.eventTable tr td:first-child {
  width: 19%;
}
.eventTable tr td:nth-child(2) {
  width: 2%;
}
.eventTable tr td:nth-child(3) {
  width: 79%;
}
h4 {
  display: inline;
  font-weight: 600 !important;
}
h5 {
  font-weight: 600 !important;
  /* this don't work - don't know why */
  margin-top: 22px;
  padding-top: 0px;
  margin-bottom: 6px;
  padding-bottom: 0px;
}
.h5_subheading {
  font-weight: 200 !important;
}
.event {
  display: flex; /* outer container for event arrow / title / description */
}
.eventTitle {
  flex-direction: column;
}
span.arrows {
  display: flex;
  flex-direction: column;
  color: #666666;
  padding-left: 0px;
  margin-left: 0px;
  cursor: pointer;
}
/* https://stackoverflow.com/q/27492191 */
.chevronArrow {
  border-right: 0.1em solid #7f7f7f;
  border-top: 0.1em solid #7f7f7f;
  width: 0.4em;
  height: 0.4em;
  margin-top: 0.4em;
  margin-left: 0em;
  margin-right: 0.6em;
  margin-bottom: 0.1em;
}
.hide {
  display: none;
}
.right {
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
}
.down {
  transform: rotate(135deg);
  -webkit-transform: rotate(135deg);
}
span.chevronArrow:hover,
.down {
  border-color: var(--brand-color);
}
/* key adjustments for small screen */
.narrow h4,
.narrow h4 {
  font-size: 1.25rem;
}
.narrow .eventTable col.first,
.medium .eventTable col.first {
  width: 8%;
}
.narrow .eventTable col.second,
.medium .eventTable col.second {
  width: 3%;
}
.narrow .eventTable col.third,
.medium .eventTable col.third {
  width: 89%;
}
.narrow .wideScreenOnly,
.medium .wideScreenOnly {
  /* show only start times on narrow screens */
  display: none;
}
ul.legend {
  margin-left: 0px;
  padding-left: 0px;
  margin-top: 12px;
  margin-bottom: 0px;
  padding-bottom: 0px;
  font-weight: 300;
}
ul.legend li {
  display: inline;
}
li + li {
  margin-left: 6px;
}
li:not(:first-of-type) + li::before {
  margin-right: 6px;
  content: " | "; /* https://stackoverflow.com/q/9171699 */
}
</style>
